/**
 * niftee AI Editor Panel
 *
 * Conversational AI editing interface for the V4 editor.
 * Allows users to make changes to their website through natural language.
 */

import { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { sendClaudeChatMessage } from '../../api/editor';

// ============================================================================
// Types
// ============================================================================

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: Array<{
    type: string;
    success?: boolean;
    error?: string;
  }>;
  isLoading?: boolean;
}

interface ClaudeChatPanelProps {
  websiteId: string;
  selectedSection?: string;
  currentPage?: string;
  onActionsExecuted?: () => void;
}

// ============================================================================
// Suggested Prompts
// ============================================================================

const SUGGESTED_PROMPTS = [
  'What sections do I have?',
  'Make the headline more compelling',
  'Suggest improvements for my services section',
  'Make the colors warmer and more inviting',
];

// ============================================================================
// Component
// ============================================================================

export function ClaudeChatPanel({
  websiteId,
  selectedSection,
  currentPage,
  onActionsExecuted,
}: ClaudeChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setError(null);
    setIsLoading(true);

    // Add loading message
    const loadingId = `loading_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: loadingId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isLoading: true,
      },
    ]);

    try {
      // Build conversation history for context
      const conversationHistory = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await sendClaudeChatMessage(
        websiteId,
        userMessage.content,
        selectedSection,
        currentPage,
        conversationHistory
      );

      // Remove loading message
      setMessages((prev) => prev.filter((m) => m.id !== loadingId));

      if (response.success) {
        // Build action summary
        const actionSummary = response.actionResults?.map((result) => ({
          type: result.action.type,
          success: result.success,
          error: result.error,
        }));

        const assistantMessage: ChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: response.reply,
          timestamp: new Date(),
          actions: actionSummary,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Trigger preview refresh if actions were executed
        if (response.actionResults?.some((r) => r.success)) {
          onActionsExecuted?.();
        }
      } else {
        setError(response.error || 'Failed to get response');
        // Remove loading message on error
        setMessages((prev) => prev.filter((m) => m.id !== loadingId));
      }
    } catch (err) {
      console.error('[ClaudeChat] Error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong');
      // Remove loading message on error
      setMessages((prev) => prev.filter((m) => m.id !== loadingId));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-[#3AABE8] to-[#2B8BC2] text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">niftee AI Editor</span>
        </div>
        <p className="text-xs text-blue-100 mt-1">
          Ask niftee to edit your site
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Sparkles className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900 mb-2">
              Welcome to niftee AI Editor
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Tell niftee what you'd like to change — text, colors, styles, and more.
              Try one of these to get started:
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTED_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => handleSuggestedPrompt(prompt)}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-900'
              }`}
            >
              {message.isLoading ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              ) : (
                <>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                  {/* Action results */}
                  {message.actions && message.actions.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Actions:</p>
                      <div className="space-y-1">
                        {message.actions.map((action, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center gap-1.5 text-xs ${
                              action.success ? 'text-green-600' : 'text-red-500'
                            }`}
                          >
                            {action.success ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                              <AlertCircle className="w-3.5 h-3.5" />
                            )}
                            <span>
                              {action.type}
                              {action.error && `: ${action.error}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-100">
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Context indicator */}
      {selectedSection && (
        <div className="px-4 py-1.5 bg-blue-50 border-t border-blue-100">
          <p className="text-xs text-blue-600">
            Context: <span className="font-medium">{selectedSection}</span>
          </p>
        </div>
      )}

      {/* Input */}
      <div className="p-4 bg-white border-t border-gray-200">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask niftee to edit your site..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
